// Filename: MustSeeWaterfallsVancouver.jsx

import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function WaterfallsNearVancouverArticle() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "must-see-waterfalls-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles?.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "shannon-falls": "Shannon Falls",
    "bridal-veil": "Bridal Veil Falls",
    "twin-falls": "Twin Falls",
    "norvan-falls": "Norvan Falls",
    "gold-creek-falls": "Gold Creek Falls",
    "cypress-falls": "Cypress Falls",
    "cleveland-dam": "Cleveland Dam",
    "dining-accommodations": "Dining & Accommodations",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://bcparks.ca",
      label: "BC Parks Official Site",
    },
    {
      url: "https://metrovancouver.org/services/parks",
      label: "Metro Vancouver Regional Parks",
    },
    {
      url: "https://www.hellobc.com",
      label: "Hello BC (Tourism Board)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each waterfall section has a unique id matching our labelsMap.
  const locations = [
    {
      id: "shannon-falls",
      lat: 49.6750,
      lng: -123.1584,
      popupText: "Shannon Falls (Squamish, BC)",
    },
    {
      id: "bridal-veil",
      lat: 49.1736,
      lng: -121.7410,
      popupText: "Bridal Veil Falls (Chilliwack, BC)",
    },
    {
      id: "twin-falls",
      lat: 49.3436,
      lng: -123.0184,
      popupText: "Twin Falls (Lynn Canyon Park)",
    },
    {
      id: "norvan-falls",
      lat: 49.4032,
      lng: -123.0088,
      popupText: "Norvan Falls (Lynn Headwaters Regional Park)",
    },
    {
      id: "gold-creek-falls",
      lat: 49.3091,
      lng: -122.5204,
      popupText: "Gold Creek Falls (Golden Ears Provincial Park)",
    },
    {
      id: "cypress-falls",
      lat: 49.3455,
      lng: -123.2016,
      popupText: "Cypress Falls (West Vancouver)",
    },
    {
      id: "cleveland-dam",
      lat: 49.3422,
      lng: -123.1157,
      popupText: "Cleveland Dam (Capilano River Regional Park)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Must-See Waterfalls Near Vancouver</title>
        <meta
          name="description"
          content="Discover must-see waterfalls near Vancouver, with practical tips, safety considerations, and family-friendly advice."
        />
        <meta
          name="keywords"
          content="Vancouver waterfalls, Shannon Falls, Bridal Veil Falls, Twin Falls, Norvan Falls, Gold Creek Falls, Cypress Falls, Cleveland Dam"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/must-see-waterfalls-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Must-See Waterfalls Near Vancouver"
        />
        <meta
          property="og:description"
          content="Explore the top waterfalls around Vancouver, complete with hiking tips and local regulations."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/must-see-waterfalls-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/must-see-waterfalls-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Must-See Waterfalls Near Vancouver"
        />
        <meta
          name="twitter:description"
          content="Plan your trip to see these stunning waterfalls near Vancouver, complete with practical tips."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/must-see-waterfalls-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A scenic waterfall near Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is well-known for its lush rainforests, mountainous
              landscapes, and breathtaking coastal views. Within a short drive or
              hike, you can find spectacular waterfalls that showcase the region’s
              natural beauty. This guide highlights seven waterfalls near Vancouver
              and provides tips on access, safety, and what to expect.
            </p>

            {/* SECTION: Shannon Falls */}
            <h2 id="shannon-falls">Shannon Falls</h2>
            <p
              ref={(el) => setSpotRef("shannon-falls", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Shannon Falls</strong>{" "}
              <Marker
                coordY={49.6750}
                coordX={-123.1584}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is the third-highest waterfall in British Columbia, towering at 335
              meters. It is easily accessible from the Sea-to-Sky Highway, making it
              a perfect stop when traveling to Squamish or Whistler.
            </p>
            <ul>
              <li>
                <strong>Trail:</strong> A short, well-maintained path leads to a
                main viewpoint.
              </li>
              <li>
                <strong>Family-Friendly:</strong> Suitable for all ages with a
                wheelchair-accessible viewing platform.
              </li>
              <li>
                <strong>Parking:</strong> Free but fills up quickly on weekends.
              </li>
            </ul>

            {/* SECTION: Bridal Veil Falls */}
            <h2 id="bridal-veil">Bridal Veil Falls</h2>
            <p
              ref={(el) => setSpotRef("bridal-veil", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Bridal Veil Falls</strong>{" "}
              <Marker
                coordY={49.1736}
                coordX={-121.7410}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is known for its veil-like appearance as the water cascades over a
              smooth rock. The short walk from the parking lot makes it an excellent
              choice for families or those seeking a quick nature break.
            </p>
            <ul>
              <li>
                <strong>Accessibility:</strong> A brief 15-minute walk on an
                easy trail.
              </li>
              <li>
                <strong>Photography Tip:</strong> Mornings often have better light
                and fewer crowds.
              </li>
            </ul>

            {/* SECTION: Twin Falls */}
            <h2 id="twin-falls">Twin Falls</h2>
            <p
              ref={(el) => setSpotRef("twin-falls", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Twin Falls</strong>{" "}
              <Marker
                coordY={49.3436}
                coordX={-123.0184}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is situated in Lynn Canyon Park, famed for its dense rainforest and
              free suspension bridge. Multiple trails lead through the park, with
              steep sections near the falls.
            </p>
            <ul>
              <li>
                <strong>Trail:</strong> Moderate, featuring some short but steep
                climbs.
              </li>
              <li>
                <strong>Parking:</strong> Limited free parking; public transit is a
                good alternative.
              </li>
            </ul>

            {/* SECTION: Norvan Falls */}
            <h2 id="norvan-falls">Norvan Falls</h2>
            <p
              ref={(el) => setSpotRef("norvan-falls", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Norvan Falls</strong>{" "}
              <Marker
                coordY={49.4032}
                coordX={-123.0088}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              offers a more secluded experience within Lynn Headwaters Regional
              Park. It’s a moderate hike, about 14 km round trip, and delivers a
              serene forest setting.
            </p>
            <ul>
              <li>
                <strong>Trail Length:</strong> Approximately 4–5 hours round trip.
              </li>
              <li>
                <strong>Essentials:</strong> Sturdy footwear, water, and snacks.
                Trails can be muddy.
              </li>
            </ul>

            {/* SECTION: Gold Creek Falls */}
            <h2 id="gold-creek-falls">Gold Creek Falls</h2>
            <p
              ref={(el) => setSpotRef("gold-creek-falls", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Gold Creek Falls</strong>{" "}
              <Marker
                coordY={49.3091}
                coordX={-122.5204}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is located in Golden Ears Provincial Park. This spot is a
              family-friendly hike with scenic forest routes and the option to
              explore nearby Alouette Lake.
            </p>
            <ul>
              <li>
                <strong>Trail:</strong> About 5.5 km round trip, mostly flat and
                well-maintained.
              </li>
              <li>
                <strong>Nearby Activities:</strong> Swimming or canoeing in
                Alouette Lake.
              </li>
            </ul>

            {/* SECTION: Cypress Falls */}
            <h2 id="cypress-falls">Cypress Falls</h2>
            <p
              ref={(el) => setSpotRef("cypress-falls", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Cypress Falls</strong>{" "}
              <Marker
                coordY={49.3455}
                coordX={-123.2016}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              lies in West Vancouver and features a short hiking trail through an
              old-growth forest. It’s less crowded than some of the other popular
              spots.
            </p>
            <ul>
              <li>
                <strong>Trail:</strong> Roughly 3 km round trip with some uneven
                terrain.
              </li>
              <li>
                <strong>Parking:</strong> Usually free street parking on Woodgreen
                Place.
              </li>
            </ul>

            {/* SECTION: Cleveland Dam */}
            <h2 id="cleveland-dam">Cleveland Dam</h2>
            <p
              ref={(el) => setSpotRef("cleveland-dam", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Cleveland Dam</strong>{" "}
              <Marker
                coordY={49.3422}
                coordX={-123.1157}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              isn’t a traditional waterfall, but the overflowing water creates a
              dramatic cascade at Capilano River Regional Park.
            </p>
            <ul>
              <li>
                <strong>Accessibility:</strong> Very short walk from the parking
                lot to the viewpoint.
              </li>
              <li>
                <strong>Scenery:</strong> Offers stunning views of Capilano Lake
                and the surrounding mountains.
              </li>
            </ul>

            {/* SECTION: Dining & Accommodations */}
            <h2 id="dining-accommodations">Dining & Accommodations</h2>
            <p>
              Each waterfall location has nearby dining or lodging options. For
              example, Squamish offers cafes and hotels near Shannon Falls, while
              Chilliwack has motels and restaurants close to Bridal Veil Falls.
              Golden Ears Provincial Park has camping for those visiting Gold
              Creek Falls. North Vancouver features various short-term rentals and
              B&Bs near Lynn Canyon (Twin Falls, Norvan Falls) and Cleveland Dam.
              In West Vancouver, Cypress Falls visitors can explore local eateries
              in Dundarave Village or head to downtown Vancouver for more
              accommodations.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a day pass or special permit?</h3>
              <p>
                Many of these waterfalls are in provincial or regional parks that
                are free to enter. Some parks (like Golden Ears) may require
                day-use permits in peak season, so always check official websites
                before heading out.
              </p>

              <h3>2. Are these waterfalls family-friendly?</h3>
              <p>
                Yes. Locations such as Shannon Falls, Bridal Veil Falls, and Gold
                Creek Falls are particularly accessible. Others, like Norvan Falls,
                involve longer hikes and require more preparation.
              </p>

              <h3>3. Can I swim at these waterfalls?</h3>
              <p>
                Swimming is not advised directly under most falls due to slippery
                rocks and strong currents. Designated swimming areas are available
                at sites like Alouette Lake near Gold Creek Falls.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Some parks have limited parking or day-pass systems during peak
              seasons. Always follow signage, stay on marked trails, and respect
              wildlife habitats. Maintain a leave-no-trace ethos by packing out all
              garbage. Keep dogs on a leash where required, and supervise children
              near water or steep drops.
            </p>
            <p>
              <em>
                Disclaimer: Conditions can change rapidly. Check official park
                resources for closures, weather advisories, or updated safety
                guidelines before visiting.
              </em>
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Discovering waterfalls near Vancouver is a fulfilling way to explore
              the region’s natural charm. From the easily accessible Shannon Falls
              to the remote tranquility of Norvan Falls, each site offers a unique
              experience. By following local regulations and best practices, you
              can ensure a safe, enjoyable trip—leaving these stunning landscapes
              preserved for future visitors.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}